import { X, Pause, Play, Trash2 } from 'lucide-react';
import { formatBytes } from '../lib/format';

interface UploadProgressProps {
  uploads: Array<{
    uploadId: string;
    fileName: string;
    fileSize: number;
    progress: number;
    uploadedChunks: number[];
    totalChunks: number;
    status: 'pending' | 'uploading' | 'completed' | 'error';
    error?: string;
  }>;
  onResume?: (uploadId: string) => void;
  onCancel?: (uploadId: string) => void;
  onRemove?: (uploadId: string) => void;
}

export function UploadProgress({ uploads, onResume, onCancel, onRemove }: UploadProgressProps) {
  if (uploads.length === 0) return null;

  return (
    <div className="glass-panel fixed bottom-4 right-4 w-96 max-h-96 overflow-y-auto rounded-2xl p-4 text-white">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold">上传进度</h3>
        <span className="text-sm text-white/60">{uploads.length} 个文件</span>
      </div>

      <div className="space-y-3">
        {uploads.map((upload) => (
          <div key={upload.uploadId} className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{upload.fileName}</p>
                <p className="text-xs text-white/60">
                  {formatBytes(upload.fileSize)} • {upload.uploadedChunks.length}/{upload.totalChunks} 分片
                </p>
              </div>
              <div className="flex items-center gap-1">
                {upload.status === 'uploading' && (
                  <button
                    className="rounded-lg p-1 text-white/60 hover:text-white"
                    onClick={() => onCancel?.(upload.uploadId)}
                    title="取消上传"
                  >
                    <Pause className="h-4 w-4" />
                  </button>
                )}
                {upload.status === 'error' && (
                  <button
                    className="rounded-lg p-1 text-white/60 hover:text-white"
                    onClick={() => onResume?.(upload.uploadId)}
                    title="恢复上传"
                  >
                    <Play className="h-4 w-4" />
                  </button>
                )}
                {(upload.status === 'completed' || upload.status === 'error') && (
                  <button
                    className="rounded-lg p-1 text-white/60 hover:text-white"
                    onClick={() => onRemove?.(upload.uploadId)}
                    title="移除"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="relative h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className={`h-full transition-all duration-300 ${
                  upload.status === 'completed'
                    ? 'bg-gradient-to-r from-green-500 to-green-400'
                    : upload.status === 'error'
                    ? 'bg-gradient-to-r from-red-500 to-red-400'
                    : 'bg-gradient-to-r from-brand to-accent'
                }`}
                style={{ width: `${upload.progress}%` }}
              />
            </div>

            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-white/60">
                {upload.status === 'completed'
                  ? '完成'
                  : upload.status === 'error'
                  ? upload.error || '上传失败'
                  : `上传中 ${upload.progress}%`}
              </span>
              <span className="text-white/40">
                {upload.uploadedChunks.length}/{upload.totalChunks} 分片
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}